import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/useAuthStore';
import { ApiError } from '../../services/api/apiClient';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister?: () => void;
}

export function LoginModal({ isOpen, onClose, onSwitchToRegister }: LoginModalProps) {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';
  const { login } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => emailRef.current?.focus(), 300);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);
    setError(null);

    try {
      await login({ email, password });
      const appUrl = import.meta.env.VITE_APP_URL;
      if (appUrl) {
        window.location.href = appUrl;
      } else {
        onClose();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError(isSpanish ? 'Email o contraseña incorrectos' : 'Invalid email or password');
        } else {
          setError(err.message);
        }
      } else {
        setError(isSpanish ? 'Error de conexión' : 'Connection error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setError(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ pointerEvents: 'auto' }}
        >
          <div className="absolute inset-0 bg-brand-darkNavy/95 backdrop-blur-sm" onClick={handleClose} />

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative z-10 w-full max-w-md mx-4"
          >
            <div className="bg-brand-navy/90 backdrop-blur-xl border border-brand-lightBlue/30 rounded-3xl p-8 shadow-2xl">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-xl hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-dark-400" />
              </button>

              <div className="text-center mb-8">
                <h2 className="text-3xl font-black text-white mb-2">
                  {isSpanish ? 'Iniciar Sesión' : 'Sign In'}
                </h2>
                <p className="text-dark-400 text-sm">
                  {isSpanish ? 'Ingresa a tu cuenta BTalentStar' : 'Access your BTalentStar account'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400 group-focus-within:text-brand-yellow transition-colors z-10" />
                  <input
                    ref={emailRef}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    className="w-full pl-12 pr-4 py-4 text-white bg-brand-darkNavy/60 border-2 border-brand-lightBlue/30 rounded-xl placeholder-dark-500 focus:outline-none focus:border-brand-yellow/60 focus:ring-2 focus:ring-brand-yellow/20 transition-all"
                    required
                  />
                </div>

                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400 group-focus-within:text-brand-yellow transition-colors z-10" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isSpanish ? 'Contraseña' : 'Password'}
                    className="w-full pl-12 pr-12 py-4 text-white bg-brand-darkNavy/60 border-2 border-brand-lightBlue/30 rounded-xl placeholder-dark-500 focus:outline-none focus:border-brand-yellow/60 focus:ring-2 focus:ring-brand-yellow/20 transition-all"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white transition-colors z-10"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center"
                  >
                    {error}
                  </motion.div>
                )}

                <motion.button
                  type="submit"
                  disabled={isLoading}
                  whileHover={!isLoading ? { scale: 1.02 } : {}}
                  whileTap={!isLoading ? { scale: 0.98 } : {}}
                  className="w-full py-4 bg-gradient-to-r from-brand-yellow to-brand-orange text-brand-darkNavy font-bold rounded-xl text-lg shadow-lg shadow-brand-yellow/20 hover:shadow-brand-yellow/40 transition-shadow disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {isSpanish ? 'Ingresando...' : 'Signing in...'}
                    </>
                  ) : (
                    isSpanish ? 'Ingresar' : 'Sign In'
                  )}
                </motion.button>
              </form>

              <div className="mt-6 text-center space-y-3">
                <button
                  onClick={() => {
                    /* TODO: implement forgot password flow */
                  }}
                  className="text-sm text-dark-400 hover:text-brand-yellow transition-colors"
                >
                  {isSpanish ? '¿Olvidaste tu contraseña?' : 'Forgot your password?'}
                </button>
                <p className="text-sm text-dark-500">
                  {isSpanish ? '¿No tienes cuenta?' : "Don't have an account?"}{' '}
                  <button
                    onClick={() => {
                      handleClose();
                      onSwitchToRegister?.();
                    }}
                    className="text-brand-yellow hover:underline font-medium"
                  >
                    {isSpanish ? 'Regístrate' : 'Sign up'}
                  </button>
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default LoginModal;
